import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { query, where, orderBy, getDocs, getDoc } from '../../firebase.config';
import { getCollectionForCompany } from '../../utils/dataAdapter';
import { getSettingsDoc } from '../../utils/appArtifacts';
import { EmployeeCashManager } from '../../utils/employeeCashManager';
import { useTableSort, SortableHeader } from '../../utils/tableSort';
import { useTablePagination } from '../../utils/tablePagination';
import PaginationControls from '../../utils/PaginationControls';
import { CollapsibleQuickTotals } from './CommonComponents';
import { exportTableAsPDF, exportTableAsExcel } from './exportUtils';
import { Wallet, TrendingUp, TrendingDown, DollarSign, CreditCard, Users, Trash2, Filter, Calendar, User, ArrowUpDown, ArrowUp, ArrowDown, FileText, Tag, Banknote, AlertCircle, Clock, Receipt } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyUtils';
import { useCompanyCurrency } from '../../hooks/useCompanyCurrency';
import { useEnhancedCompany } from '../../contexts/EnhancedCompanyContext';
import { useNotification } from '../../contexts/NotificationContext';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from '../shared/AppSelect';

const EmployeeCashFlowReport = ({ db, userId, appId, companyId, dateRange, loading, setLoading, companyDetails }) => {
  const { employees } = useEnhancedCompany();
  const { currency: companyCurrency } = useCompanyCurrency();
  const { success, error, warning } = useNotification();
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState({
    totalInflow: { cash: 0, upi: 0, total: 0 },
    totalOutflow: { cash: 0, upi: 0, total: 0 },
    employees: {}
  });
  const [employeeDetails, setEmployeeDetails] = useState({});
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('all'); // 'all', 'cash', 'upi'
  const [selectedTransactionType, setSelectedTransactionType] = useState('all'); // 'all', 'inflow', 'outflow'
  const [selectedCategory, setSelectedCategory] = useState('all'); // 'all' | category-name
  const [includeInactiveHistory, setIncludeInactiveHistory] = useState(false);
  const [currentBalances, setCurrentBalances] = useState({});
  const [openingBalances, setOpeningBalances] = useState({}); // Opening balances before date range
  const [employeeIdToDocIdMap, setEmployeeIdToDocIdMap] = useState({}); // Map employeeId (EMP004) to document ID
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { transactionId, transaction }
  const [deleting, setDeleting] = useState(false);
  const [exportFormat, setExportFormat] = useState('transaction-list'); // 'transaction-list' | 'party-wise' | 'gst-filing'
  /** null = not scanned yet; array = last scan result */
  const [orphanList, setOrphanList] = useState(null);
  const [orphanScanLoading, setOrphanScanLoading] = useState(false);
  const [orphanRepairLoading, setOrphanRepairLoading] = useState(false);
  /** Gated from Settings → Advanced (default off) */
  const [showOrphanRepairUi, setShowOrphanRepairUi] = useState(false);

  // Table sorting - will be used on filtered data
  // Default: date descending (newest first)
  const { sortConfig, handleSort, getSortedData } = useTableSort(reportData || [], { key: 'date', direction: 'desc' });

  // Format date
  const formatDate = (dateValue) => {
    if (!dateValue) return '';
    if (dateValue?.toDate) {
      // Firestore Timestamp
      return dateValue.toDate().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    if (dateValue instanceof Date) {
      // Native Date object
      return dateValue.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    if (typeof dateValue === 'string') {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
      }
      return dateValue; // Return as-is if invalid date string
    }
    // Fallback: convert to string
    return String(dateValue);
  };

  // DD-MM-YYYY for GST export
  const formatDateGst = (dateValue) => {
    const s = formatDate(dateValue);
    return s ? s.replace(/\//g, '-') : '';
  };

  // Actual party in the transaction (who gave/received money), for GST export — not the employee whose ledger it is
  const getCounterpartyName = (row) => {
    if (row.paymentMode === 'cash' && row.cashDetails) {
      const from = row.cashDetails.cashReceivedFrom || '';
      const to = row.cashDetails.cashGivenTo || '';
      if (row.transactionType === 'inflow' && from) return from;
      if (row.transactionType === 'outflow' && to) return to;
    }
    if (row.salaryPaidForEmployeeName) return row.salaryPaidForEmployeeName;
    if (row.description && String(row.description).trim()) {
      const desc = String(row.description).trim();
      // Many descriptions are like: "Payment PRI... - Bharti University, Durg"
      // We want to group by the party name at the end, not the full description.
      const dashSep = desc.lastIndexOf(' - ');
      if (dashSep !== -1 && dashSep + 3 < desc.length) {
        return desc.slice(dashSep + 3).trim();
      }
      const enDashSep = desc.lastIndexOf(' – ');
      if (enDashSep !== -1 && enDashSep + 3 < desc.length) {
        return desc.slice(enDashSep + 3).trim();
      }
      return desc;
    }
    if (row.referenceNumber && String(row.referenceNumber).trim()) return String(row.referenceNumber).trim();
    return row.employeeName || '';
  };

  // Fetch employee cash flow data
  const fetchData = async () => {
      if (!db || !userId || !appId || !companyId) return;
      
      setLoading(true);
      try {
        const companyData = { companyId, userId, appId };
        
        // Get date range - convert to Timestamp for Firestore queries
        let startDate = null;
        let endDate = null;
        
        if (dateRange?.start) {
          const start = new Date(dateRange.start);
          start.setHours(0, 0, 0, 0);
          startDate = start.toISOString().split('T')[0];
        }
        
        if (dateRange?.end) {
          const end = new Date(dateRange.end);
          end.setHours(23, 59, 59, 999);
          endDate = end.toISOString().split('T')[0];
        }

        // Fetch all employee cash transactions
        const filters = {};
        if (startDate && endDate) {
          filters.startDate = startDate;
          filters.endDate = endDate;
        }

        // Get summary
        const summaryResult = await EmployeeCashManager.getCashFlowSummary(companyData, filters);
        if (summaryResult.success) {
          setSummary(summaryResult.data);
        }

        // Fetch all transactions
        let transactionsQuery = getCollectionForCompany(db, companyData, 'employeeCashTransactions');
        
        if (startDate && endDate) {
          // Use string dates for query (Firestore will handle conversion)
          transactionsQuery = query(
            transactionsQuery,
            where('date', '>=', startDate),
            where('date', '<=', endDate),
            orderBy('date', 'desc')
          );
        } else {
          transactionsQuery = query(transactionsQuery, orderBy('date', 'desc'));
        }

        const transactionsSnap = await getDocs(transactionsQuery);
        const transactions = [];
        const empDetails = {};
        const balances = {};
        const empIdToDocId = {}; // Map employeeId (EMP004) to document ID

        // Fetch employee details and current balances
        for (const emp of employees || []) {
          const balance = await EmployeeCashManager.getEmployeeBalance(companyData, emp.id);
          balances[emp.id] = balance;
          empDetails[emp.id] = {
            name: emp.name || '',
            employeeId: emp.employeeId || '',
            designation: emp.designation || '',
            department: emp.department || ''
          };
          // Create mapping from employeeId (display ID) to document ID
          if (emp.employeeId) {
            empIdToDocId[emp.employeeId] = emp.id;
          }
        }

        // Calculate opening balances (transactions before start date)
        const openingBalancesCalc = {};
        if (startDate) {
          try {
            // Fetch all transactions before start date to calculate opening balance
            // Note: This query might require a composite index in Firestore
            // If it fails, we'll calculate opening balance differently
            const openingTransactionsQuery = query(
              getCollectionForCompany(db, companyData, 'employeeCashTransactions'),
              where('date', '<', startDate),
              orderBy('date', 'asc')
            );
            
            const openingSnap = await getDocs(openingTransactionsQuery);
            const openingDocs = openingSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            const openingSets = await EmployeeCashManager.getSourceExistenceSetsForEmployeeTransactions(companyData, openingDocs);
            openingDocs.forEach(transaction => {
              if (!EmployeeCashManager.isEmployeeCashTransactionReportingValid(transaction, openingSets)) return;

              const empDocId = transaction.employeeId; // Document ID
              
              if (!openingBalancesCalc[empDocId]) {
                openingBalancesCalc[empDocId] = { total: 0, cash: 0, upi: 0 };
              }
              
              const amount = parseFloat(transaction.amount) || 0;
              if (transaction.transactionType === 'inflow') {
                if (transaction.paymentMode === 'cash') {
                  openingBalancesCalc[empDocId].cash += amount;
                  openingBalancesCalc[empDocId].total += amount;
                } else if (transaction.paymentMode === 'upi') {
                  openingBalancesCalc[empDocId].upi += amount;
                  openingBalancesCalc[empDocId].total += amount;
                }
              } else if (transaction.transactionType === 'outflow') {
                if (transaction.paymentMode === 'cash') {
                  openingBalancesCalc[empDocId].cash -= amount;
                  openingBalancesCalc[empDocId].total -= amount;
                } else if (transaction.paymentMode === 'upi') {
                  openingBalancesCalc[empDocId].upi -= amount;
                  openingBalancesCalc[empDocId].total -= amount;
                }
              }
            });
          } catch (error) {
            console.warn('Could not calculate opening balances (may need Firestore index):', error.message);
            // If query fails (e.g., no composite index), fetch all transactions and filter client-side
            try {
              const allTransactionsQuery = query(
                getCollectionForCompany(db, companyData, 'employeeCashTransactions'),
                orderBy('date', 'asc')
              );
              const allSnap = await getDocs(allTransactionsQuery);
              const beforeStart = [];
              allSnap.forEach(docSnap => {
                const transaction = { id: docSnap.id, ...docSnap.data() };
                const transactionDate = transaction.date;
                let txDate = null;
                
                if (transactionDate?.toDate) {
                  txDate = transactionDate.toDate();
                } else if (typeof transactionDate === 'string') {
                  txDate = new Date(transactionDate);
                }
                
                if (txDate && txDate < new Date(startDate)) {
                  beforeStart.push(transaction);
                }
              });
              const openingSetsFb = await EmployeeCashManager.getSourceExistenceSetsForEmployeeTransactions(companyData, beforeStart);
              beforeStart.forEach(transaction => {
                if (!EmployeeCashManager.isEmployeeCashTransactionReportingValid(transaction, openingSetsFb)) return;

                const empDocId = transaction.employeeId;
                  
                if (!openingBalancesCalc[empDocId]) {
                  openingBalancesCalc[empDocId] = { total: 0, cash: 0, upi: 0 };
                }
                  
                const amount = parseFloat(transaction.amount) || 0;
                if (transaction.transactionType === 'inflow') {
                  if (transaction.paymentMode === 'cash') {
                    openingBalancesCalc[empDocId].cash += amount;
                    openingBalancesCalc[empDocId].total += amount;
                  } else if (transaction.paymentMode === 'upi') {
                    openingBalancesCalc[empDocId].upi += amount;
                    openingBalancesCalc[empDocId].total += amount;
                  }
                } else if (transaction.transactionType === 'outflow') {
                  if (transaction.paymentMode === 'cash') {
                    openingBalancesCalc[empDocId].cash -= amount;
                    openingBalancesCalc[empDocId].total -= amount;
                  } else if (transaction.paymentMode === 'upi') {
                    openingBalancesCalc[empDocId].upi -= amount;
                    openingBalancesCalc[empDocId].total -= amount;
                  }
                }
              });
            } catch (fallbackError) {
              console.error('Fallback opening balance calculation also failed:', fallbackError);
              // Set all opening balances to 0 if both methods fail
            }
          }
        }
        
        setOpeningBalances(openingBalancesCalc);

        const transactionMap = new Map();
        transactionsSnap.forEach(docSnap => {
          const transaction = { id: docSnap.id, ...docSnap.data() };
          transactionMap.set(docSnap.id, transaction);
        });

        const allTxForValidity = Array.from(transactionMap.values());
        const sourceSets = await EmployeeCashManager.getSourceExistenceSetsForEmployeeTransactions(companyData, allTxForValidity);

        // Process transactions - exclude orphaned (deleted payment/salary/labour)
        transactionMap.forEach((transaction, transactionId) => {
          if (!EmployeeCashManager.isEmployeeCashTransactionReportingValid(transaction, sourceSets)) {
            return;
          }
          
          // Store document ID before overwriting - needed for filtering and balance lookups
          const empDocId = transaction.employeeId; // This is the document ID from database
          
          // Add employee details
          transaction.employeeName = empDetails[empDocId]?.name || 'Unknown';
          transaction.employeeDocId = empDocId; // Store document ID for filtering
          transaction.employeeId = empDetails[empDocId]?.employeeId || ''; // Display ID (like "EMP004")
          transaction.employeeDesignation = empDetails[empDocId]?.designation || '';
          transaction.employeeDepartment = empDetails[empDocId]?.department || '';
          
          // Add current balance using document ID
          transaction.currentBalance = balances[empDocId] || { total: 0, cash: 0, upi: 0 };
          
          // Ensure createdAt is available for sorting (use date as fallback if createdAt not present)
          if (!transaction.createdAt && transaction.date) {
            transaction.createdAt = transaction.date;
          }
          
          transactions.push(transaction);
        });

        // Sort chronologically (date asc, then createdAt asc) so same-day order is correct
        // and "Balance After" column reads in narrative order (avoids confusion when e.g.
        // inflow then outflow on same day).
        const getTime = (tx) => {
          const d = tx.date;
          if (!d) return 0;
          if (d?.toDate) return d.toDate().getTime();
          if (typeof d === 'string') return new Date(d).getTime();
          return 0;
        };
        const getCreated = (tx) => {
          const c = tx.createdAt;
          if (!c) return getTime(tx);
          if (c?.toDate) return c.toDate().getTime();
          if (typeof c === 'string') return new Date(c).getTime();
          return 0;
        };
        transactions.sort((a, b) => {
          const tA = getTime(a), tB = getTime(b);
          if (tA !== tB) return tA - tB;
          return getCreated(a) - getCreated(b);
        });

        setEmployeeDetails(empDetails);
        setCurrentBalances(balances);
        setEmployeeIdToDocIdMap(empIdToDocId);
        setReportData(transactions);
      } catch (error) {
        console.error('Error fetching employee cash flow data:', error);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchData();
  }, [db, userId, appId, companyId, dateRange, employees]);

  useEffect(() => {
    let cancelled = false;
    const loadOrphanPref = async () => {
      if (!userId) {
        setShowOrphanRepairUi(false);
        return;
      }
      try {
        const snap = await getDoc(getSettingsDoc(userId));
        if (cancelled) return;
        setShowOrphanRepairUi(!!(snap.exists() && snap.data().showEmployeeCashFlowOrphanTools));
      } catch {
        if (!cancelled) setShowOrphanRepairUi(false);
      }
    };
    loadOrphanPref();
    const onSettingsChanged = () => { loadOrphanPref(); };
    if (typeof window !== 'undefined') {
      window.addEventListener('acctoo-user-settings-changed', onSettingsChanged);
    }
    return () => {
      cancelled = true;
      if (typeof window !== 'undefined') {
        window.removeEventListener('acctoo-user-settings-changed', onSettingsChanged);
      }
    };
  }, [userId]);

  // Handle delete transaction
  const handleDeleteTransaction = async () => {
    if (!deleteConfirm) return;
    
    const { transactionId, transaction } = deleteConfirm;
    setDeleting(true);
    
    try {
      const companyData = { companyId, userId, appId };
      const result = await EmployeeCashManager.deleteCashTransaction(companyData, transactionId);
      
      if (result.success) {
        success('Transaction deleted successfully', {
          title: 'Transaction Deleted',
          details: `Amount: ${formatCurrency(transaction.amount, companyCurrency)} • Employee: ${transaction.employeeName}`,
          duration: 5000
        });
        
        // Refresh data
        setDeleteConfirm(null);
        await fetchData();
      } else {
        error(result.error || 'Failed to delete transaction', {
          title: 'Delete Failed',
          duration: 6000
        });
      }
    } catch (err) {
      console.error('Error deleting transaction:', err);
      error('Failed to delete transaction: ' + err.message, {
        title: 'Delete Failed',
        duration: 6000
      });
    } finally {
      setDeleting(false);
    }
  };

  // Close modal on Escape key when open
  useEffect(() => {
    const onKeyDown = (e) => {
      if (deleteConfirm && e.key === 'Escape' && !deleting) setDeleteConfirm(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [deleteConfirm, deleting]);

  const runOrphanScan = async () => {
    if (!companyId || !userId || !appId) {
      warning('Company context is not ready.');
      return;
    }
    setOrphanScanLoading(true);
    try {
      const companyData = { companyId, userId, appId };
      const r = await EmployeeCashManager.findOrphanEmployeeCashTransactions(companyData);
      if (r.success) {
        setOrphanList(r.orphans);
        if (r.orphans.length === 0) {
          success('No orphaned wallet entries found.', { title: 'Scan complete' });
        } else {
          success(
            `Found ${r.orphans.length} wallet row(s) whose source payment or salary was deleted. Review the table below.`,
            { title: 'Scan complete', duration: 7000 }
          );
        }
      } else {
        error(r.error || 'Scan failed', { title: 'Scan failed' });
        setOrphanList([]);
      }
    } catch (e) {
      console.error(e);
      error(e.message || 'Scan failed', { title: 'Scan failed' });
      setOrphanList([]);
    } finally {
      setOrphanScanLoading(false);
    }
  };

  const runOrphanRepair = async () => {
    if (!companyId || !userId || !appId || !orphanList?.length) return;
    if (
      !window.confirm(
        `Fix ${orphanList.length} orphaned wallet transaction(s)? Employee balances will be reversed and these rows will be removed. This cannot be undone.`
      )
    ) {
      return;
    }
    setOrphanRepairLoading(true);
    try {
      const companyData = { companyId, userId, appId };
      const r = await EmployeeCashManager.repairOrphanEmployeeCashTransactions(companyData);
      if (r.success) {
        success(
          `Repaired ${r.repairedCount} orphaned transaction(s). Employee balances were updated.`,
          { title: 'Repair complete', duration: 6000 }
        );
        setOrphanList([]);
        await fetchData();
      } else {
        if (r.repairedCount > 0) {
          warning(
            `Repaired ${r.repairedCount} but some failed: ${r.error || ''}`,
            { title: 'Partial repair', duration: 10000 }
          );
        } else {
          error(r.error || 'Repair failed', { title: 'Repair failed', duration: 8000 });
        }
        await fetchData();
        await runOrphanScan();
      }
    } catch (e) {
      console.error(e);
      error(e.message || 'Repair failed', { title: 'Repair failed' });
    } finally {
      setOrphanRepairLoading(false);
    }
  };

  // Filter data based on selections
  const activeEmployeeDocIds = useMemo(() => {
    const ids = new Set();
    (employees || []).forEach((emp) => {
      const status = (emp?.status || 'active').toLowerCase();
      if (status === 'active') ids.add(emp.id);
    });
    return ids;
  }, [employees]);

  const filteredData = useMemo(() => {
    if (!reportData || !Array.isArray(reportData)) {
      return [];
    }
    
    let filtered = [...reportData];

    // Default behavior: keep report focused on active employees.
    // Toggle can include inactive/deleted historical entries when needed.
    if (!includeInactiveHistory) {
      filtered = filtered.filter(t => activeEmployeeDocIds.has(t.employeeDocId));
    }

    if (selectedEmployee !== 'all') {
      // Use employeeDocId for filtering (document ID), not employeeId (display ID)
      filtered = filtered.filter(t => t.employeeDocId === selectedEmployee);
    }

    if (selectedPaymentMode !== 'all') {
      filtered = filtered.filter(t => t.paymentMode === selectedPaymentMode);
    }

    if (selectedTransactionType !== 'all') {
      filtered = filtered.filter(t => t.transactionType === selectedTransactionType);
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => (t.category || 'General') === selectedCategory);
    }

    return filtered;
  }, [
    reportData,
    includeInactiveHistory,
    activeEmployeeDocIds,
    selectedEmployee,
    selectedPaymentMode,
    selectedTransactionType,
    selectedCategory
  ]);

  const categoryOptions = useMemo(() => {
    const categories = new Set(['General']);
    (reportData || []).forEach((t) => {
      const c = String(t?.category || '').trim();
      if (c) categories.add(c);
    });
    if (!categories.has('Service Vendor Payment')) categories.add('Service Vendor Payment');
    return Array.from(categories).sort((a, b) => a.localeCompare(b));
  }, [reportData]);

  // Update sorted data when filters change - this is used for display
  // When sorted by Date, we sort by date then createdAt so same-day order is consistent
  const sortedData = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) {
      return [];
    }
    const isDateSort = sortConfig.key === 'date' || !sortConfig.key;
    if (isDateSort) {
      const getTime = (tx) => {
        const d = tx?.date;
        if (!d) return 0;
        if (d?.toDate) return d.toDate().getTime();
        if (typeof d === 'string') return new Date(d).getTime();
        return 0;
      };
      const getCreated = (tx) => {
        const c = tx?.createdAt;
        if (!c) return 0;
        if (c?.toDate) return c.toDate().getTime();
        if (typeof c === 'string') return new Date(c).getTime();
        return 0;
      };
      const asc = sortConfig.key === 'date' && sortConfig.direction === 'asc';
      return [...filteredData].sort((a, b) => {
        const tA = getTime(a), tB = getTime(b);
        if (tA !== tB) return asc ? tA - tB : tB - tA;
        const cA = getCreated(a), cB = getCreated(b);
        return asc ? cA - cB : cB - cA;
      });
    }
    return getSortedData(filteredData);
  }, [filteredData, sortConfig, getSortedData]);
  
  // Pagination based on sorted/filtered data
  const paginationData = useTablePagination(sortedData, 25);

  // Calculate filtered summary
  const filteredSummary = useMemo(() => {
    const filtered = {
      totalInflow: { cash: 0, upi: 0, total: 0 },
      totalOutflow: { cash: 0, upi: 0, total: 0 },
      employees: {}
    };

    if (!filteredData || !Array.isArray(filteredData)) {
      return filtered;
    }

    filteredData.forEach(transaction => {
      const amount = parseFloat(transaction.amount) || 0;
      // Use employeeId (display ID like "EMP004") for grouping in summary
      // This ensures employees are grouped by their display ID consistently
      const empDisplayId = transaction.employeeId || '';
      const mode = transaction.paymentMode;
      const type = transaction.transactionType;

      if (!filtered.employees[empDisplayId]) {
        filtered.employees[empDisplayId] = {
          name: transaction.employeeName,
          employeeId: transaction.employeeId,
          employeeDocId: transaction.employeeDocId, // Store doc ID for balance lookup
          inflow: { cash: 0, upi: 0, total: 0 },
          outflow: { cash: 0, upi: 0, total: 0 }
        };
      }

      if (type === 'inflow') {
        if (mode === 'cash') {
          filtered.totalInflow.cash += amount;
          filtered.employees[empDisplayId].inflow.cash += amount;
        } else if (mode === 'upi') {
          filtered.totalInflow.upi += amount;
          filtered.employees[empDisplayId].inflow.upi += amount;
        }
        filtered.totalInflow.total += amount;
        filtered.employees[empDisplayId].inflow.total += amount;
      } else if (type === 'outflow') {
        if (mode === 'cash') {
          filtered.totalOutflow.cash += amount;
          filtered.employees[empDisplayId].outflow.cash += amount;
        } else if (mode === 'upi') {
          filtered.totalOutflow.upi += amount;
          filtered.employees[empDisplayId].outflow.upi += amount;
        }
        filtered.totalOutflow.total += amount;
        filtered.employees[empDisplayId].outflow.total += amount;
      }
    });

    return filtered;
  }, [filteredData]);

  // Summary cards data
  const summaryCards = useMemo(() => {
    const fs = filteredSummary || {
      totalInflow: { cash: 0, upi: 0, total: 0 },
      totalOutflow: { cash: 0, upi: 0, total: 0 },
      employees: {}
    };
    return [
    {
      title: 'Total Inflow',
      value: formatCurrency(fs.totalInflow?.total || 0, companyCurrency),
      subtitle: `Cash: ${formatCurrency(fs.totalInflow?.cash || 0, companyCurrency)} | UPI: ${formatCurrency(fs.totalInflow?.upi || 0, companyCurrency)}`,
      icon: TrendingUp,
      color: 'green'
    },
    {
      title: 'Total Outflow',
      value: formatCurrency(fs.totalOutflow?.total || 0, companyCurrency),
      subtitle: `Cash: ${formatCurrency(fs.totalOutflow?.cash || 0, companyCurrency)} | UPI: ${formatCurrency(fs.totalOutflow?.upi || 0, companyCurrency)}`,
      icon: TrendingDown,
      color: 'red'
    },
    {
      title: 'Net Flow',
      value: formatCurrency((fs.totalInflow?.total || 0) - (fs.totalOutflow?.total || 0), companyCurrency),
      subtitle: `Period: ${dateRange?.start ? formatDate(dateRange.start) : ''} to ${dateRange?.end ? formatDate(dateRange.end) : ''}`,
      icon: DollarSign,
      color: 'blue'
    },
    {
      title: 'Active Employees',
      value: fs.employees ? Object.keys(fs.employees).length : 0,
      subtitle: `${filteredData?.length || 0} transactions`,
      icon: Users,
      color: 'purple'
    }
  ];
  }, [filteredSummary, filteredData, companyCurrency, dateRange]);

  // Table columns
  const columns = [
    {
      key: 'date',
      label: 'Date',
      sortable: true,
      icon: Calendar,
      render: (row) => formatDate(row.date)
    },
    {
      key: 'employeeName',
      label: 'Employee',
      sortable: true,
      icon: User,
      render: (row) => (
        <div>
          <div className="font-medium">{row.employeeName || 'Unknown'}</div>
          <div className="text-xs text-gray-500">{row.employeeId || ''} {row.employeeDesignation ? `• ${row.employeeDesignation}` : ''}</div>
        </div>
      )
    },
    {
      key: 'transactionType',
      label: 'Type',
      sortable: true,
      icon: ArrowUpDown,
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          row.transactionType === 'inflow' 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {row.transactionType === 'inflow' ? 'Inflow' : 'Outflow'}
        </span>
      )
    },
    {
      key: 'paymentMode',
      label: 'Mode',
      sortable: true,
      icon: CreditCard,
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          row.paymentMode === 'cash' 
            ? 'bg-yellow-100 text-yellow-800' 
            : 'bg-blue-100 text-blue-800'
        }`}>
          {row.paymentMode === 'cash' ? 'Cash' : 'UPI'}
        </span>
      )
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      icon: DollarSign,
      align: 'right',
      render: (row) => (
        <span className={`font-semibold ${
          row.transactionType === 'inflow' ? 'text-green-600' : 'text-red-600'
        }`}>
          {row.transactionType === 'inflow' ? '+' : '-'}{formatCurrency(row.amount, companyCurrency)}
        </span>
      )
    },
    {
      key: 'description',
      label: 'Description',
      sortable: false,
      icon: FileText,
      cellClassName: 'px-4 py-3 max-w-xs w-64',
      headerClassName: 'max-w-xs w-64',
      render: (row) => (
        <div className="max-w-xs w-64 break-words">
          {row.salarySourceType === 'other_employee_wallet' && row.salaryPaidForEmployeeName && (
            <div className="text-xs text-amber-700 font-medium mb-0.5">Salary for: {row.salaryPaidForEmployeeName}</div>
          )}
          <div className="text-sm break-words break-all">{row.description || '-'}</div>
          {row.referenceNumber && (
            <div className="text-xs text-gray-500 break-words break-all">Ref: {row.referenceNumber}</div>
          )}
          {row.linkedReceiptNumber && (
            <div className="text-xs text-blue-600 break-words break-all">Receipt: {row.linkedReceiptNumber}</div>
          )}
          {row.linkedLoanId && (
            <div className="text-xs text-purple-600 break-words break-all">Loan Payment: {row.linkedLoanId}</div>
          )}
        </div>
      )
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      icon: Tag,
      render: (row) => (
        <span>
          {row.category || 'General'}
          {row.salarySourceType === 'other_employee_wallet' && (
            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" title="Settled from this employee's wallet for another employee's salary">Other wallet</span>
          )}
          {row.linkedPaymentType === 'labour' && (
            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800" title="Labour/Advance payment settled from employee wallet">Labour</span>
          )}
          {row.linkedPaymentType === 'expense' && (
            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800" title="Fixed/Variable expense settled from employee wallet">Expense</span>
          )}
        </span>
      )
    },
    {
      key: 'balance',
      label: 'Balance (after tx)',
      sortable: true,
      icon: Wallet,
      align: 'right',
      title: 'Balance after this transaction. Table is sorted by date (oldest first) so this column reads in order.',
      render: (row) => formatCurrency(row.balance || 0, companyCurrency)
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      icon: null,
      render: (row) => (
        <button
          onClick={() => setDeleteConfirm({ transactionId: row.id, transaction: row })}
          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
          title="Delete transaction"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )
    }
  ];

  // Export column definitions for PDF/Excel (keys must match exportData row keys)
  const exportColumns = [
    { key: 'Date', label: 'Date' },
    { key: 'Employee', label: 'Employee' },
    { key: 'Employee ID', label: 'Employee ID' },
    { key: 'Type', label: 'Type' },
    { key: 'Mode', label: 'Mode' },
    { key: 'Amount', label: 'Amount' },
    { key: 'Description', label: 'Description' },
    { key: 'Reference', label: 'Reference' },
    { key: 'Category', label: 'Category' },
    { key: 'Salary Source', label: 'Salary Source' },
    { key: 'Salary Paid For', label: 'Salary Paid For' },
    { key: 'Balance After', label: 'Balance After' }
  ];

  const buildExportData = () => {
    if (!sortedData || !Array.isArray(sortedData)) return [];
    return sortedData.map(row => ({
      'Date': formatDate(row.date),
      'Employee': row.employeeName,
      'Employee ID': row.employeeId || '',
      'Type': row.transactionType === 'inflow' ? 'Inflow' : 'Outflow',
      'Mode': row.paymentMode === 'cash' ? 'Cash' : 'UPI',
      'Amount': row.amount,
      'Description': row.description || '',
      'Reference': row.referenceNumber || '',
      'Category': row.category || 'General',
      ...(row.salarySourceType === 'other_employee_wallet' && {
        'Salary Source': 'Other employee wallet',
        'Salary Paid For': row.salaryPaidForEmployeeName || ''
      }),
      'Balance After': row.balance ?? 0
    }));
  };

  const handleExportPDF = () => {
    const exportData = buildExportData();
    if (!exportData.length) return;
    const title = `Employee Cash/UPI Flow Report - ${dateRange?.start ? formatDate(dateRange.start) : ''} to ${dateRange?.end ? formatDate(dateRange.end) : ''}`;
    const filename = `Employee_Cash_UPI_Flow_${dateRange?.start ? formatDate(dateRange.start).replace(/\//g, '_') : ''}_${dateRange?.end ? formatDate(dateRange.end).replace(/\//g, '_') : ''}.pdf`;
    exportTableAsPDF({
      data: exportData,
      columns: exportColumns,
      filename,
      title,
      companyDetails: companyDetails || {},
      reportDetails: dateRange?.start && dateRange?.end
        ? { Period: `${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}` }
        : undefined
    });
  };

  const handleExportExcel = () => {
    if (!sortedData || !Array.isArray(sortedData)) return;
    const filename = `Employee_Cash_UPI_Flow_${dateRange?.start ? formatDate(dateRange.start).replace(/\//g, '_') : ''}_${dateRange?.end ? formatDate(dateRange.end).replace(/\//g, '_') : ''}`;

    if (exportFormat === 'party-wise') {
      // Party-wise: Employee name, total, sub-list of date + amount
      const escapeCsv = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      let csvContent = '';
      if (companyDetails?.firmName) csvContent += `${escapeCsv(companyDetails.firmName)}\n`;
      if (companyDetails?.address) csvContent += `${escapeCsv(companyDetails.address)}\n`;
      if (dateRange?.start && dateRange?.end) {
        csvContent += `Period,${escapeCsv(`${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}`)}\n`;
      }
      csvContent += '\n';

      // Group by employee (use employeeId as key for consistency)
      const byEmployee = {};
      sortedData.forEach(row => {
        const key = row.employeeId || row.employeeName || 'Unknown';
        if (!byEmployee[key]) {
          byEmployee[key] = { name: row.employeeName || 'Unknown', employeeId: row.employeeId || '', transactions: [] };
        }
        const signedAmount = row.transactionType === 'inflow' ? (row.amount || 0) : -(row.amount || 0);
        byEmployee[key].transactions.push({
          date: formatDate(row.date),
          amount: signedAmount,
          type: row.transactionType === 'inflow' ? 'Inflow' : 'Outflow',
          mode: row.paymentMode === 'cash' ? 'Cash' : 'UPI',
          description: row.description || ''
        });
      });

      Object.entries(byEmployee).forEach(([, emp]) => {
        const total = emp.transactions.reduce((sum, t) => sum + t.amount, 0);
        csvContent += `Party (Employee),Employee ID,Total Amount\n`;
        csvContent += `${escapeCsv(emp.name)},${escapeCsv(emp.employeeId)},${total}\n`;
        csvContent += `Date,Amount,Type,Mode,Description\n`;
        emp.transactions.forEach(t => {
          csvContent += `${escapeCsv(t.date)},${t.amount},${escapeCsv(t.type)},${escapeCsv(t.mode)},${escapeCsv(t.description)}\n`;
        });
        csvContent += '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}_party_wise.csv`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } else if (exportFormat === 'gst-filing') {
      // GST Filing: separate format — (1) Date, Amount, Flow, Payment Method, Party Name + total; (2) Party-wise: date, amount, flow per transaction
      const escapeCsv = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      let csvContent = '';

      if (companyDetails?.firmName) csvContent += `${escapeCsv(companyDetails.firmName)}\n`;
      if (companyDetails?.address) csvContent += `${escapeCsv(companyDetails.address)}\n`;
      if (dateRange?.start && dateRange?.end) {
        csvContent += `Period,${escapeCsv(`${formatDateGst(dateRange.start)} to ${formatDateGst(dateRange.end)}`)}\n`;
      }
      csvContent += '\n';

      // ——— Part 1: GST style transaction list (Date, Amount, Flow, Payment Method, Party Name) ———
      csvContent += 'GST Filing - Transaction List\n\n';
      csvContent += 'Date,Amount,Flow,Payment Method,Party Name\n';

      let totalInflow = 0;
      let totalOutflow = 0;
      sortedData.forEach(row => {
        const flow = row.transactionType === 'inflow' ? 'Inflow' : 'Outflow';
        const amt = Number(row.amount) || 0;
        if (row.transactionType === 'inflow') totalInflow += amt;
        else totalOutflow += amt;
        csvContent += `${escapeCsv(formatDateGst(row.date))},${amt},${escapeCsv(flow)},${escapeCsv(row.paymentMode === 'cash' ? 'Cash' : 'UPI')},${escapeCsv(getCounterpartyName(row))}\n`;
      });
      csvContent += '\n';
      csvContent += `Total Inflow,${totalInflow},,,\n`;
      csvContent += `Total Outflow,${totalOutflow},,,\n`;

      // ——— Part 2: Party-wise (for each party: Date, Amount, Flow, Payment Method per transaction) ———
      csvContent += '\n\nGST Filing - Party-wise Data\n\n';

      const byParty = {};
      sortedData.forEach(row => {
        const partyName = getCounterpartyName(row) || 'Unknown';
        if (!byParty[partyName]) {
          byParty[partyName] = { transactions: [] };
        }
        byParty[partyName].transactions.push({
          date: formatDateGst(row.date),
          amount: row.amount || 0,
          flow: row.transactionType === 'inflow' ? 'Inflow' : 'Outflow',
          mode: row.paymentMode === 'cash' ? 'Cash' : 'UPI'
        });
      });

      Object.entries(byParty).forEach(([partyName, data]) => {
        // For GST party-wise section we care about total credited (inflow) from this party
        const inflowTx = data.transactions.filter(t => t.flow === 'Inflow');
        if (inflowTx.length === 0) {
          return;
        }
        const totalCredited = inflowTx.reduce((sum, t) => sum + (t.amount || 0), 0);

        csvContent += `Party,${escapeCsv(partyName)}\n`;
        csvContent += `Total Credited,${totalCredited}\n`;

        // Sub-list: each credit transaction from this party (date + amount)
        csvContent += 'Date,Amount\n';
        inflowTx.forEach(t => {
          csvContent += `${escapeCsv(t.date)},${t.amount}\n`;
        });

        csvContent += '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${filename}_GST_filing.csv`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } else {
      const exportData = buildExportData();
      if (!exportData.length) return;
      exportTableAsExcel({
        data: exportData,
        columns: exportColumns,
        filename,
        companyDetails: companyDetails || {},
        reportDetails: dateRange?.start && dateRange?.end
          ? { Period: `${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}` }
          : undefined
      });
    }
  };

  // Employee-wise summary section
  const employeeSummaryRows = useMemo(() => {
    if (!filteredSummary || !filteredSummary.employees) {
      return [];
    }
    
    // Get first transaction date for each employee for chronological sorting
    const employeeFirstDates = {};
    filteredData.forEach(transaction => {
      const empDisplayId = transaction.employeeId || '';
      if (empDisplayId && !employeeFirstDates[empDisplayId]) {
        const dateValue = transaction.date;
        if (dateValue?.toDate) {
          employeeFirstDates[empDisplayId] = dateValue.toDate();
        } else if (typeof dateValue === 'string') {
          employeeFirstDates[empDisplayId] = new Date(dateValue);
        } else {
          employeeFirstDates[empDisplayId] = new Date(0); // Fallback
        }
      }
    });
    
    const rows = Object.entries(filteredSummary.employees).map(([empDisplayId, data]) => {
      // empDisplayId here is the employeeId (display ID like "EMP004"), not the document ID
      // Use employeeDocId from data if available, otherwise map from employeeIdToDocIdMap
      const docId = data.employeeDocId || employeeIdToDocIdMap[empDisplayId] || empDisplayId;
      const currentBalance = currentBalances[docId] || { total: 0, cash: 0, upi: 0 };
      const openingBalance = openingBalances[docId] || { total: 0, cash: 0, upi: 0 };
      const netFlow = data.inflow.total - data.outflow.total;
      
      // Closing balance = Opening Balance + Net Flow (for the period)
      const closingBalance = {
        total: openingBalance.total + netFlow,
        cash: openingBalance.cash + (data.inflow.cash - data.outflow.cash),
        upi: openingBalance.upi + (data.inflow.upi - data.outflow.upi)
      };
      
      return {
        employeeId: data.employeeId,
        employeeName: data.name,
        totalInflow: data.inflow.total,
        cashInflow: data.inflow.cash,
        upiInflow: data.inflow.upi,
        totalOutflow: data.outflow.total,
        cashOutflow: data.outflow.cash,
        upiOutflow: data.outflow.upi,
        netFlow: netFlow,
        openingBalance: openingBalance.total,
        openingCashBalance: openingBalance.cash,
        openingUpiBalance: openingBalance.upi,
        closingBalance: closingBalance.total,
        closingCashBalance: closingBalance.cash,
        closingUpiBalance: closingBalance.upi,
        currentBalance: currentBalance.total, // Actual current balance from DB
        currentCashBalance: currentBalance.cash,
        currentUpiBalance: currentBalance.upi,
        firstTransactionDate: employeeFirstDates[empDisplayId] || new Date(0)
      };
    });
    
    // Sort chronologically by first transaction date (oldest first)
    rows.sort((a, b) => {
      if (a.firstTransactionDate && b.firstTransactionDate) {
        return a.firstTransactionDate - b.firstTransactionDate;
      }
      // Fallback to employee name if dates are equal
      return (a.employeeName || '').localeCompare(b.employeeName || '');
    });
    
    return rows;
  }, [filteredSummary, currentBalances, openingBalances, employeeIdToDocIdMap, filteredData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading employee cash flow data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Wallet className="w-6 h-6 text-purple-600" />
              Employee Cash/UPI Flow Report
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Comprehensive report of all employee cash and UPI transactions
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleExportPDF}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
            >
              Export PDF
            </button>
            <div className="flex items-center gap-2">
              <AppSelect
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
                aria-label="Export format"
                sheetTitle="Export format"
                options={[
                  { value: 'transaction-list', label: 'Transaction list' },
                  { value: 'party-wise', label: 'Party-wise (name, total, sub-list)' },
                  { value: 'gst-filing', label: 'GST filing (table + party-wise)' },
                ]}
                triggerClassName={`${APP_SELECT_FILTER_TRIGGER} w-auto focus:ring-green-500 bg-white`}
              />
              <button
                onClick={handleExportExcel}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
              >
                Export Excel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Period totals — tap Show Totals to reveal */}
      <CollapsibleQuickTotals cards={summaryCards} />

      {/* Orphan wallet repair — only if enabled in Settings → Advanced */}
      {showOrphanRepairUi && (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex gap-3 min-w-0">
            <AlertCircle className="w-6 h-6 text-amber-700 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="font-semibold text-amber-950">Orphaned wallet transactions</h3>
              <p className="text-sm text-amber-900 mt-1 leading-relaxed">
                Finds employee cash/UPI rows that still point to a payment, salary, or irregular entry that was deleted
                elsewhere. Those rows are hidden from this report but can leave balances wrong. Scan lists them; Fix
                reverses balances and removes the rows (same as deleting each line manually).
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 flex-shrink-0">
            <button
              type="button"
              disabled={orphanScanLoading || orphanRepairLoading || !companyId}
              onClick={runOrphanScan}
              className="px-4 py-2 bg-white border border-amber-300 text-amber-950 rounded-md text-sm font-medium hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {orphanScanLoading ? 'Scanning…' : 'Scan for orphans'}
            </button>
            <button
              type="button"
              disabled={
                orphanRepairLoading ||
                orphanScanLoading ||
                !orphanList?.length ||
                !companyId
              }
              onClick={runOrphanRepair}
              className="px-4 py-2 bg-amber-800 text-white rounded-md text-sm font-medium hover:bg-amber-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {orphanRepairLoading ? 'Fixing…' : 'Fix all (reverse & delete)'}
            </button>
          </div>
        </div>

        {orphanList !== null && (
          <div className="mt-4 pt-4 border-t border-amber-200">
            {orphanList.length === 0 ? (
              <p className="text-sm text-green-800 font-medium">Last scan: no orphaned entries.</p>
            ) : (
              <>
                <p className="text-sm text-amber-950 font-medium mb-2">
                  {orphanList.length} row(s) — review IDs and amounts before fixing.
                </p>
                <div className="overflow-x-auto max-h-56 overflow-y-auto rounded border border-amber-200 bg-white">
                  <table className="min-w-full text-sm">
                    <thead className="bg-amber-100/80 sticky top-0 text-left text-amber-950">
                      <tr>
                        <th className="px-3 py-2 font-medium">Date</th>
                        <th className="px-3 py-2 font-medium">Employee</th>
                        <th className="px-3 py-2 font-medium">Type</th>
                        <th className="px-3 py-2 font-medium text-right">Amount</th>
                        <th className="px-3 py-2 font-medium">Reason</th>
                        <th className="px-3 py-2 font-medium">Links</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-100">
                      {orphanList.map((row) => (
                        <tr key={row.id} className="text-gray-800">
                          <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.date)}</td>
                          <td className="px-3 py-2">
                            <div className="font-medium">{row.employeeName || '—'}</div>
                            <div className="text-xs text-gray-500 font-mono truncate max-w-[10rem]" title={row.employeeId}>
                              {row.employeeId}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span
                              className={
                                row.transactionType === 'inflow'
                                  ? 'text-green-700'
                                  : 'text-red-700'
                              }
                            >
                              {row.transactionType === 'inflow' ? 'In' : 'Out'} · {row.paymentMode || '—'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono">
                            {formatCurrency(row.amount, companyCurrency)}
                          </td>
                          <td className="px-3 py-2 text-xs max-w-xs">{row.reason}</td>
                          <td className="px-3 py-2 text-xs font-mono break-all max-w-[14rem]">
                            {row.linkedPaymentId && (
                              <div title="linkedPaymentId">pay: {row.linkedPaymentId}</div>
                            )}
                            {row.salaryId && (
                              <div title="salaryId">sal: {row.salaryId}</div>
                            )}
                            {row.linkedReceiptNumber && (
                              <div className="text-gray-600">rcpt: {row.linkedReceiptNumber}</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {orphanList === null && (
          <p className="mt-3 text-xs text-amber-800/90">Run a scan to trace wallet rows with missing source documents.</p>
        )}
      </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center mb-4">
          <Filter className="w-5 h-5 text-gray-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
        </div>
        
        {/* Date Range Display */}
        {dateRange?.start && dateRange?.end && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-blue-900">Date Range:</span>
              <span className="text-sm text-blue-700">
                {formatDate(dateRange.start)} to {formatDate(dateRange.end)}
              </span>
            </div>
          </div>
        )}
        
        <div className="mb-3">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={includeInactiveHistory}
              onChange={(e) => setIncludeInactiveHistory(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Include inactive/deleted employee history
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
            <AppSelect
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              aria-label="Employee"
              sheetTitle="Employee"
              options={[
                { value: 'all', label: 'All Employees' },
                ...(employees && Array.isArray(employees)
                  ? employees
                      .filter((emp) => includeInactiveHistory || ((emp.status || 'active').toLowerCase() === 'active'))
                      .map((emp) => ({
                        value: emp.id,
                        label: `${emp.name} (${emp.employeeId || emp.id})`,
                      }))
                  : []),
              ]}
              triggerClassName={APP_SELECT_FILTER_TRIGGER}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
            <AppSelect
              value={selectedPaymentMode}
              onChange={(e) => setSelectedPaymentMode(e.target.value)}
              aria-label="Payment Mode"
              sheetTitle="Payment Mode"
              options={[
                { value: 'all', label: 'All Modes' },
                { value: 'cash', label: 'Cash Only' },
                { value: 'upi', label: 'UPI Only' },
              ]}
              triggerClassName={APP_SELECT_FILTER_TRIGGER}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Type</label>
            <AppSelect
              value={selectedTransactionType}
              onChange={(e) => setSelectedTransactionType(e.target.value)}
              aria-label="Transaction Type"
              sheetTitle="Transaction Type"
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'inflow', label: 'Inflow Only' },
                { value: 'outflow', label: 'Outflow Only' },
              ]}
              triggerClassName={APP_SELECT_FILTER_TRIGGER}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <AppSelect
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              aria-label="Category"
              sheetTitle="Category"
              options={[
                { value: 'all', label: 'All Categories' },
                ...categoryOptions.map((cat) => ({
                  value: cat,
                  label: cat === 'Service Vendor Payment' ? 'Service Vendor Payment (New)' : cat,
                })),
              ]}
              triggerClassName={APP_SELECT_FILTER_TRIGGER}
            />
          </div>
        </div>
      </div>

      {/* Employee-wise Summary */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-indigo-50">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            Employee-wise Summary
          </h3>
        </div>
        
        {/* Mobile Card View */}
        <div className="block md:hidden p-3 space-y-3">
          {employeeSummaryRows && employeeSummaryRows.length > 0 ? (
            employeeSummaryRows.map((row, idx) => (
              <div key={idx} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-200 shadow-sm">
                {/* Employee header with avatar */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md">
                    {(row.employeeName || 'E').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-gray-900 truncate">{row.employeeName}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {row.employeeId}
                    </div>
                  </div>
                  <div className={`text-right ${row.netFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    <div className="text-xs font-medium opacity-75">Net Flow</div>
                    <div className="text-lg font-bold flex items-center gap-1">
                      {row.netFlow >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                      {formatCurrency(Math.abs(row.netFlow), companyCurrency)}
                    </div>
                  </div>
                </div>
                
                {/* Balance cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                  <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                      <Calendar className="w-3 h-3" /> Opening
                    </div>
                    <div className="font-semibold text-gray-800">{formatCurrency(row.openingBalance, companyCurrency)}</div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-3 rounded-lg border border-blue-100 shadow-sm">
                    <div className="flex items-center gap-1 text-xs text-blue-600 mb-1">
                      <Wallet className="w-3 h-3" /> Closing
                    </div>
                    <div className="font-bold text-blue-700">{formatCurrency(row.closingBalance, companyCurrency)}</div>
                  </div>
                </div>
                
                {/* Flow cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200 shadow-sm">
                    <div className="flex items-center gap-1.5 text-xs text-green-700 font-medium mb-1">
                      <TrendingUp className="w-3.5 h-3.5" /> Inflow
                    </div>
                    <div className="font-bold text-green-600 text-base">{formatCurrency(row.totalInflow, companyCurrency)}</div>
                    <div className="text-[10px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                      <span className="flex items-center gap-0.5"><Banknote className="w-3 h-3" />{formatCurrency(row.cashInflow, companyCurrency)}</span>
                      <span className="text-gray-300">|</span>
                      <span className="flex items-center gap-0.5"><CreditCard className="w-3 h-3" />{formatCurrency(row.upiInflow, companyCurrency)}</span>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-red-50 to-rose-50 p-3 rounded-lg border border-red-200 shadow-sm">
                    <div className="flex items-center gap-1.5 text-xs text-red-700 font-medium mb-1">
                      <TrendingDown className="w-3.5 h-3.5" /> Outflow
                    </div>
                    <div className="font-bold text-red-600 text-base">{formatCurrency(row.totalOutflow, companyCurrency)}</div>
                    <div className="text-[10px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                      <span className="flex items-center gap-0.5"><Banknote className="w-3 h-3" />{formatCurrency(row.cashOutflow, companyCurrency)}</span>
                      <span className="text-gray-300">|</span>
                      <span className="flex items-center gap-0.5"><CreditCard className="w-3 h-3" />{formatCurrency(row.upiOutflow, companyCurrency)}</span>
                    </div>
                  </div>
                </div>
                
                {Math.abs(row.closingBalance - row.currentBalance) > 0.01 && (
                  <div className="mt-3 p-2 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-700 flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Actual Balance: <strong>{formatCurrency(row.currentBalance, companyCurrency)}</strong></span>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="py-12 text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No employee data available</p>
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Opening Balance</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cash Inflow</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">UPI Inflow</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Inflow</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cash Outflow</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">UPI Outflow</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Outflow</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Net Flow</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Closing Balance</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {employeeSummaryRows && employeeSummaryRows.length > 0 ? (
                employeeSummaryRows.map((row, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{row.employeeName}</div>
                    <div className="text-xs text-gray-500">{row.employeeId}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-700">
                    {formatCurrency(row.openingBalance, companyCurrency)}
                    <div className="text-xs text-gray-500">
                      Cash: {formatCurrency(row.openingCashBalance, companyCurrency)} | UPI: {formatCurrency(row.openingUpiBalance, companyCurrency)}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-green-600">
                    {formatCurrency(row.cashInflow, companyCurrency)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-green-600">
                    {formatCurrency(row.upiInflow, companyCurrency)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-green-600">
                    {formatCurrency(row.totalInflow, companyCurrency)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-red-600">
                    {formatCurrency(row.cashOutflow, companyCurrency)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-red-600">
                    {formatCurrency(row.upiOutflow, companyCurrency)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-semibold text-red-600">
                    {formatCurrency(row.totalOutflow, companyCurrency)}
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap text-right text-sm font-semibold ${
                    row.netFlow >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(row.netFlow, companyCurrency)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-bold text-blue-600">
                    {formatCurrency(row.closingBalance, companyCurrency)}
                    <div className="text-xs text-gray-500">
                      Cash: {formatCurrency(row.closingCashBalance, companyCurrency)} | UPI: {formatCurrency(row.closingUpiBalance, companyCurrency)}
                    </div>
                    {Math.abs(row.closingBalance - row.currentBalance) > 0.01 && (
                      <div className="text-xs text-orange-600 mt-1">
                        Actual: {formatCurrency(row.currentBalance, companyCurrency)}
                      </div>
                    )}
                  </td>
                </tr>
              ))
              ) : (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                    No employee data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transaction Details Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Transaction Details
            </h3>
            {sortConfig.key === 'date' && (
              <p className="text-xs text-gray-500 mt-1">
                Sorted by Date (newest first){sortConfig.direction === 'desc' ? '' : ', oldest first'} • Balance column = after each transaction
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchData()}
              disabled={loading}
              className="text-sm px-3 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
              title="Refresh to see latest transactions (e.g. after deleting from Salary tab)"
            >
              {loading ? '…' : '↻'} Refresh
            </button>
            <div className="text-sm text-gray-600 bg-white px-3 py-1 rounded-full border border-gray-200">
              {paginationData?.currentData?.length || 0} of {sortedData?.length || 0} transactions
            </div>
          </div>
        </div>
        
        {/* Mobile Card View */}
        <div className="block md:hidden p-3 space-y-3">
          {!paginationData?.currentData || paginationData.currentData.length === 0 ? (
            <div className="py-12 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No transactions found</p>
              <p className="text-gray-400 text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            paginationData.currentData.map((row, idx) => (
              <div key={row.id || idx} className={`rounded-xl overflow-hidden border shadow-sm ${
                row.transactionType === 'inflow' 
                  ? 'border-green-200 bg-gradient-to-br from-green-50/50 to-white' 
                  : 'border-red-200 bg-gradient-to-br from-red-50/50 to-white'
              }`}>
                {/* Colored top bar */}
                <div className={`h-1 ${row.transactionType === 'inflow' ? 'bg-gradient-to-r from-green-400 to-emerald-500' : 'bg-gradient-to-r from-red-400 to-rose-500'}`}></div>
                
                <div className="p-3">
                  <div className="flex items-start gap-3">
                    {/* Transaction type icon */}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      row.transactionType === 'inflow' 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-red-100 text-red-600'
                    }`}>
                      {row.transactionType === 'inflow' 
                        ? <TrendingUp className="w-5 h-5" /> 
                        : <TrendingDown className="w-5 h-5" />
                      }
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      {/* Badges */}
                      <div className="flex items-center gap-1.5 flex-wrap mb-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          row.transactionType === 'inflow' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {row.transactionType === 'inflow' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
                          {row.transactionType === 'inflow' ? 'IN' : 'OUT'}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          row.paymentMode === 'cash' 
                            ? 'bg-amber-100 text-amber-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {row.paymentMode === 'cash' ? <Banknote className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}
                          {row.paymentMode === 'cash' ? 'Cash' : 'UPI'}
                        </span>
                      </div>
                      
                      {/* Employee info */}
                      <div className="text-sm font-bold text-gray-900">{row.employeeName}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {row.employeeId} {row.employeeDesignation ? `• ${row.employeeDesignation}` : ''}
                      </div>
                    </div>
                    
                    {/* Amount */}
                    <div className="text-right">
                      <div className={`text-lg font-bold ${
                        row.transactionType === 'inflow' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {row.transactionType === 'inflow' ? '+' : '-'}{formatCurrency(row.amount, companyCurrency)}
                      </div>
                      <div className="text-xs text-gray-400 flex items-center justify-end gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(row.date)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Description section */}
                  {(row.description || row.referenceNumber || row.linkedReceiptNumber) && (
                    <div className="mt-3 p-2.5 bg-white rounded-lg border border-gray-100 text-xs">
                      {row.description && (
                        <div className="text-gray-700 break-words flex items-start gap-1.5">
                          <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                          <span>{row.description}</span>
                        </div>
                      )}
                      {row.referenceNumber && (
                        <div className="text-gray-500 mt-1.5 flex items-center gap-1.5">
                          <Tag className="w-3.5 h-3.5" />
                          Ref: {row.referenceNumber}
                        </div>
                      )}
                      {row.linkedReceiptNumber && (
                        <div className="text-blue-600 mt-1.5 flex items-center gap-1.5">
                          <Receipt className="w-3.5 h-3.5" />
                          Receipt: {row.linkedReceiptNumber}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Footer */}
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <Tag className="w-3.5 h-3.5" />
                      <span className="font-medium">{row.category || 'General'}</span>
                    </div>
                    <button
                      onClick={() => setDeleteConfirm({ transactionId: row.id, transaction: row })}
                      className="p-2.5 text-red-500 hover:text-red-700 hover:bg-red-50 active:bg-red-100 rounded-xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
                      title="Delete transaction"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map(column => (
                  <SortableHeader
                    key={column.key}
                    columnKey={column.key}
                    label={column.label}
                    icon={column.icon}
                    align={column.align || 'left'}
                    headerClassName={column.headerClassName || ''}
                    sortConfig={sortConfig}
                    onSort={column.sortable !== false ? handleSort : undefined}
                  />
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {!paginationData?.currentData || paginationData.currentData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">
                    No transactions found for the selected filters
                  </td>
                </tr>
              ) : (
                paginationData.currentData.map((row, idx) => (
                  <tr key={row.id || idx} className="hover:bg-gray-50">
                    {columns.map(column => (
                      <td 
                        key={column.key} 
                        className={column.cellClassName || 'px-4 py-3 whitespace-nowrap'}
                      >
                        {column.render ? column.render(row) : row[column.key]}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <PaginationControls {...paginationData} />
      </div>

      {/* Delete Confirmation Modal - rendered in portal so it stays in viewport when scrolled */}
      {deleteConfirm && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[9999] p-0 sm:p-4"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          onClick={() => !deleting && setDeleteConfirm(null)}
        >
          <div 
            className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Transaction</h3>
              <p className="text-sm text-gray-600 mb-2">
                Are you sure you want to delete this transaction?
              </p>
              <div className="bg-gray-50 p-3 rounded-md mb-4">
                <div className="text-sm">
                  <div className="font-medium text-gray-900">Employee:</div>
                  <div className="text-gray-700">{deleteConfirm.transaction.employeeName} ({deleteConfirm.transaction.employeeId})</div>
                </div>
                <div className="text-sm mt-2">
                  <div className="font-medium text-gray-900">Amount:</div>
                  <div className={`font-semibold ${deleteConfirm.transaction.transactionType === 'inflow' ? 'text-green-600' : 'text-red-600'}`}>
                    {deleteConfirm.transaction.transactionType === 'inflow' ? '+' : '-'}{formatCurrency(deleteConfirm.transaction.amount, companyCurrency)}
                  </div>
                </div>
                <div className="text-sm mt-2">
                  <div className="font-medium text-gray-900">Date:</div>
                  <div className="text-gray-700">{formatDate(deleteConfirm.transaction.date)}</div>
                </div>
                {deleteConfirm.transaction.description && (
                  <div className="text-sm mt-2">
                    <div className="font-medium text-gray-900">Description:</div>
                    <div className="text-gray-700">{deleteConfirm.transaction.description}</div>
                  </div>
                )}
              </div>
              <p className="text-xs text-yellow-600 mb-4">
                ⚠️ This will reverse the balance effect and cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteTransaction}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default EmployeeCashFlowReport;

